import * as yup from 'yup';
import {
  EIN_REGEX,
  FOREIGN_ENTITY_REGISTRATION_REGEX,
  FOREIGN_PASSPORT_REGEX,
  FOREIGN_TAX_ID_REGEX,
  SSN_ITIN_REGEX,
} from '../../constants/validation-patterns';
import {
  requiresStateAndPostalCode,
  isValidStateForCountry,
  validatePostalCodeFormat,
  isUSTerritory,
  isUS,
} from '@agentnet/utils';

export enum TransferorType {
  INDIVIDUAL = 'Individual',
  ENTITY = 'Entity',
  TRUST = 'Trust',
}

export enum PersonType {
  INDIVIDUAL = 'individual',
  LEGAL_ENTITY = 'legalEntity',
}

export enum IdentificationType {
  SSN_ITIN = 'ssn-itin',
  EIN = 'ein',
  FOREIGN_TAX_ID = 'foreignTaxId',
  FOREIGN_PASSPORT = 'foreignPassport',
  FOREIGN_ENTITY_REGISTRATION_NUMBER = 'foreignEntityRegistrationNumber',
  NO_IDENTIFICATION = 'noIdentification',
}

export enum AddressType {
  PRIMARY = 'primary',
  FOREIGN = 'foreign',
}

const requiredFieldMessage = (fieldName: string): string => `${fieldName} is a required field.`;

const isForeignBusinessToggleOn = (value: boolean | string | undefined | null): boolean =>
  value === true || value === 'true';

export const addressSchema = yup.object().shape({
  address1: yup
    .string()
    .test('max-length', 'Address 1 cannot exceed 100 characters', function (value) {
      if (!value) return true;
      const trimmedValue = typeof value === 'string' ? value.trim() : value;
      if (trimmedValue === '') return true;
      return trimmedValue.length <= 100;
    })
    .trim()
    .notRequired(),
  address2: yup
    .string()
    .test('max-length', 'Address 2 cannot exceed 100 characters', function (value) {
      if (!value) return true;
      const trimmedValue = typeof value === 'string' ? value.trim() : value;
      if (trimmedValue === '') return true;
      return trimmedValue.length <= 100;
    })
    .trim()
    .notRequired(),
  city: yup
    .string()
    .test('max-length', 'City cannot exceed 50 characters', function (value) {
      if (!value) return true;
      const trimmedValue = typeof value === 'string' ? value.trim() : value;
      if (trimmedValue === '') return true;
      return trimmedValue.length <= 50;
    })
    .trim()
    .notRequired(),
  country: yup.string().notRequired(),
  state: yup.string().when('country', {
    is: (country: string) => requiresStateAndPostalCode(country),
    then: (schema) =>
      schema
        .trim()
        .notRequired()
        .test('valid-state-for-country', function (value) {
          const { country } = this.parent;
          if (!value || !country) return true;

          if (!isValidStateForCountry(value, country)) {
            return this.createError({
              message: 'Invalid state/province code for the selected country',
            });
          }
          return true;
        }),
    otherwise: (schema) => schema.trim().notRequired(),
  }),
  postalCode: yup
    .string()
    .test('max-length', 'Postal Code cannot exceed 9 characters', function (value) {
      if (!value) return true;
      const trimmedValue = typeof value === 'string' ? value.trim() : value;
      if (trimmedValue === '') return true;
      const { country } = this.parent;
      // US and US Territories allow ZIP+4 format (10 chars with hyphen)
      if (isUS(country) || isUSTerritory(country)) {
        return trimmedValue.length <= 10;
      }
      return trimmedValue.length <= 9;
    })
    .test('format-validation', function (value) {
      if (!value) return true;
      const trimmedValue = typeof value === 'string' ? value.trim() : value;
      if (trimmedValue === '') return true;
      const { country } = this.parent;

      const error = validatePostalCodeFormat(trimmedValue, country);
      if (error) {
        return this.createError({ message: error });
      }
      return true;
    })
    .trim()
    .when('country', {
      is: (country: string) => requiresStateAndPostalCode(country),
      then: (schema) => schema.notRequired(),
      otherwise: (schema) => schema.notRequired(),
    }),
});

const createIdentificationSchema = () =>
  yup.object().shape({
    type: yup.string().required(requiredFieldMessage('Identification Type')),
    number: yup.string().when('type', {
      is: (value: string) => value && value !== IdentificationType.NO_IDENTIFICATION,
      then: (schema) =>
        schema
          .required(requiredFieldMessage('Identification Number'))
          .test('format-validation', function (value) {
            const { type } = this.parent;
            if (!value) return true;

            const trimmedValue = value.trim();

            if (type === IdentificationType.EIN) {
              if (!EIN_REGEX.test(trimmedValue)) {
                return this.createError({
                  message: 'EIN must be in XX-XXXXXXX format (e.g., 12-3456789)',
                });
              }
            }

            if (type === IdentificationType.SSN_ITIN) {
              // Basic format check first
              if (!SSN_ITIN_REGEX.test(trimmedValue)) {
                return this.createError({
                  message: 'SSN/ITIN must be in XXX-XX-XXXX format (e.g., 123-45-6789)',
                });
              }
            }

            if (type === IdentificationType.FOREIGN_TAX_ID) {
              if (!FOREIGN_TAX_ID_REGEX.test(trimmedValue)) {
                return this.createError({
                  message: 'Foreign Tax ID must be 6-20 alphanumeric characters only (e.g., ABC123456)',
                });
              }
            }

            if (type === IdentificationType.FOREIGN_PASSPORT) {
              if (!FOREIGN_PASSPORT_REGEX.test(trimmedValue)) {
                return this.createError({
                  message: 'Foreign Passport Number must be 6-9 alphanumeric characters only (e.g., A1234567)',
                });
              }
            }

            if (type === IdentificationType.FOREIGN_ENTITY_REGISTRATION_NUMBER) {
              if (!FOREIGN_ENTITY_REGISTRATION_REGEX.test(trimmedValue)) {
                return this.createError({
                  message:
                    'Foreign Entity Registration Number must be 5-20 alphanumeric characters only (e.g., ABC12345)',
                });
              }
            }

            return true;
          })
          .max(50, 'Identification Number cannot exceed 50 characters'),
      otherwise: (schema) => schema.notRequired(),
    }),
    jurisdiction: yup.string().when('type', {
      is: (value: string) =>
        value &&
        (value === IdentificationType.FOREIGN_TAX_ID ||
          value === IdentificationType.FOREIGN_PASSPORT ||
          value === IdentificationType.FOREIGN_ENTITY_REGISTRATION_NUMBER),
      then: (schema) => schema.required(requiredFieldMessage('Issuing Jurisdiction')),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

export const identificationSchema = createIdentificationSchema();

export const trusteeIdentificationSchema = createIdentificationSchema();

export const individualSchema = yup.object().shape({
  firstName: yup
    .string()
    .trim()
    .required(requiredFieldMessage('First Name'))
    .max(50, 'First Name cannot exceed 50 characters'),
  middleName: yup.string().trim().max(50, 'Middle Name cannot exceed 50 characters').notRequired(),
  lastName: yup
    .string()
    .trim()
    .required(requiredFieldMessage('Last Name'))
    .max(50, 'Last Name cannot exceed 50 characters'),
  suffix: yup.string().trim().max(20, 'Suffix cannot exceed 20 characters').notRequired(),
  dateOfBirth: yup
    .date()
    .transform((value, originalValue) => {
      return originalValue === '' ? undefined : value;
    })
    .max(new Date(), 'Date of Birth cannot be in the future')
    .typeError('Please enter a valid date')
    .required(requiredFieldMessage('Date of Birth')),
});

export const entitySchema = yup.object().shape({
  entityLegalName: yup
    .string()
    .trim()
    .required(requiredFieldMessage('Entity Legal Name'))
    .max(130, 'Entity Legal Name cannot exceed 130 characters'),
  alternateNames: yup
    .array()
    .of(yup.string().trim().max(150, 'Alternate Name cannot exceed 150 characters'))
    .max(5, 'Maximum of 5 alternate names allowed')
    .notRequired(),
});

export const trustSchema = yup.object().shape({
  entityLegalName: yup
    .string()
    .trim()
    .required(requiredFieldMessage('Trust Legal Name'))
    .max(130, 'Trust Legal Name cannot exceed 130 characters'),
  alternateNames: yup
    .array()
    .of(yup.string().trim().max(150, 'Alternate Name cannot exceed 150 characters'))
    .max(5, 'Maximum of 5 alternate names allowed')
    .notRequired(),
  dateTrustExecuted: yup
    .date()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .max(new Date(), 'Date Trust Instrument was Executed cannot be in the future')
    .typeError('Please enter a valid date')
    .required(requiredFieldMessage('Date Trust Instrument was Executed')),
});

export const trusteeSchema = yup.object().shape({
  personType: yup
    .string()
    .oneOf([PersonType.INDIVIDUAL, PersonType.LEGAL_ENTITY])
    .required(requiredFieldMessage('Person Type')),

  firstName: yup
    .string()
    .trim()
    .max(50, 'First Name cannot exceed 50 characters')
    .when('personType', {
      is: PersonType.INDIVIDUAL,
      then: (schema) => schema.required(requiredFieldMessage('First Name')),
      otherwise: (schema) => schema.notRequired(),
    }),
  middleName: yup.string().trim().max(50, 'Middle Name cannot exceed 50 characters').notRequired(),
  lastName: yup
    .string()
    .trim()
    .max(50, 'Last Name cannot exceed 50 characters')
    .when('personType', {
      is: PersonType.INDIVIDUAL,
      then: (schema) => schema.required(requiredFieldMessage('Last Name')),
      otherwise: (schema) => schema.notRequired(),
    }),
  suffix: yup.string().trim().max(20, 'Suffix cannot exceed 20 characters').notRequired(),
  dateOfBirth: yup
    .date()
    .transform((value, originalValue) => {
      return originalValue === '' ? undefined : value;
    })
    .when('personType', {
      is: PersonType.INDIVIDUAL,
      then: (schema) =>
        schema
          .max(new Date(), 'Date of Birth cannot be in the future')
          .typeError('Please enter a valid date')
          .required(requiredFieldMessage('Date of Birth')),
      otherwise: (schema) => schema.notRequired(),
    }),

  entityLegalName: yup
    .string()
    .trim()
    .max(130, 'Entity Legal Name cannot exceed 130 characters')
    .when('personType', {
      is: PersonType.LEGAL_ENTITY,
      then: (schema) => schema.required(requiredFieldMessage('Entity Legal Name')),
      otherwise: (schema) => schema.notRequired(),
    }),
  alternateNames: yup
    .mixed()
    .nullable()
    .when('personType', {
      is: PersonType.LEGAL_ENTITY,
      then: (schema) =>
        schema.test('max-length-per-name', 'Alternate Name cannot exceed 150 characters', function (value) {
          if (!value) return true;
          let names: string[] = [];
          if (typeof value === 'string') {
            names = value.split(',').map((name) => name.trim());
          } else if (Array.isArray(value)) {
            names = value.map((name) => (typeof name === 'string' ? name.trim() : ''));
          } else {
            return true;
          }
          return names.every((name) => name.length <= 150);
        }),
      otherwise: (schema) => schema.notRequired(),
    }),

  address: yup.object().when(['personType', 'addressType', 'foreignBusinessNoUSLocation'], {
    is: (personType: string, addressType: string, foreignBusinessNoUSLocation: boolean | string) =>
      personType === PersonType.INDIVIDUAL ||
      (personType === PersonType.LEGAL_ENTITY &&
        addressType === AddressType.FOREIGN &&
        !isForeignBusinessToggleOn(foreignBusinessNoUSLocation)),
    then: () =>
      yup.object().shape({
        address1: yup
          .string()
          .trim()
          .required(requiredFieldMessage('Address 1'))
          .max(100, 'Address 1 cannot exceed 100 characters'),
        address2: yup.string().trim().max(100, 'Address 2 cannot exceed 100 characters').notRequired(),
        city: yup.string().trim().required(requiredFieldMessage('City')).max(50, 'City cannot exceed 50 characters'),
        country: yup.string().required(requiredFieldMessage('Country/Jurisdiction')),
        state: yup.string().when('country', {
          is: (country: string) => requiresStateAndPostalCode(country || 'US'),
          then: (schema) =>
            schema
              .trim()
              .required(requiredFieldMessage('State/U.S. Territory'))
              .test('valid-state-for-country', function (value) {
                const { country } = this.parent;
                if (!value || !country) return true;

                if (!isValidStateForCountry(value, country)) {
                  return this.createError({
                    message: 'Invalid state/province code for the selected country',
                  });
                }
                return true;
              }),
          otherwise: (schema) => schema.trim().notRequired(),
        }),
        postalCode: yup
          .string()
          .trim()
          .test('max-length', 'Postal Code cannot exceed 9 characters', function (value) {
            if (!value) return true;
            const trimmedValue = typeof value === 'string' ? value.trim() : value;
            if (trimmedValue === '') return true;
            const { country } = this.parent;
            // US and US Territories allow ZIP+4 format (10 chars with hyphen)
            if (isUS(country) || isUSTerritory(country)) {
              return trimmedValue.length <= 10;
            }
            return trimmedValue.length <= 9;
          })
          .test('format-validation', function (value) {
            if (!value) return true;
            const trimmedValue = typeof value === 'string' ? value.trim() : value;
            if (trimmedValue === '') return true;
            const { country } = this.parent;

            const error = validatePostalCodeFormat(trimmedValue, country);
            if (error) {
              return this.createError({ message: error });
            }
            return true;
          })
          .when('country', {
            is: (country: string) => requiresStateAndPostalCode(country || 'US'),
            then: (schema) => schema.required(requiredFieldMessage('ZIP/Postal Code')),
            otherwise: (schema) => schema.notRequired(),
          }),
      }),
    otherwise: () => addressSchema,
  }),
  identification: trusteeIdentificationSchema,

  addressType: yup.string().when(['personType', 'foreignBusinessNoUSLocation'], {
    is: (personType: string, foreignBusinessNoUSLocation: boolean | string) =>
      personType === PersonType.LEGAL_ENTITY && !isForeignBusinessToggleOn(foreignBusinessNoUSLocation),
    then: (schema) => schema.required(requiredFieldMessage('Address Type')),
    otherwise: (schema) => schema.notRequired(),
  }),

  foreignBusinessNoUSLocation: yup.boolean().notRequired(),
  foreignBusinessAddress: yup.object().when(['personType', 'foreignBusinessNoUSLocation'], {
    is: (personType: string, foreignBusinessNoUSLocation: boolean | string) =>
      personType === PersonType.LEGAL_ENTITY && isForeignBusinessToggleOn(foreignBusinessNoUSLocation),
    then: () =>
      yup.object().shape({
        address1: yup
          .string()
          .trim()
          .required(requiredFieldMessage('Address 1'))
          .max(100, 'Address 1 cannot exceed 100 characters'),
        address2: yup.string().trim().max(100, 'Address 2 cannot exceed 100 characters').notRequired(),
        city: yup.string().trim().required(requiredFieldMessage('City')).max(50, 'City cannot exceed 50 characters'),
        country: yup.string().required(requiredFieldMessage('Country/Jurisdiction')),
        state: yup.string().when('country', {
          is: (country: string) => requiresStateAndPostalCode(country || 'US'),
          then: (schema) =>
            schema
              .trim()
              .required(requiredFieldMessage('State/U.S. Territory'))
              .test('valid-state-for-country', function (value) {
                const { country } = this.parent;
                if (!value || !country) return true;

                if (!isValidStateForCountry(value, country)) {
                  return this.createError({
                    message: 'Invalid state/province code for the selected country',
                  });
                }
                return true;
              }),
          otherwise: (schema) => schema.trim().notRequired(),
        }),
        postalCode: yup
          .string()
          .trim()
          .test('max-length', 'Postal Code cannot exceed 9 characters', function (value) {
            if (!value) return true;
            const trimmedValue = typeof value === 'string' ? value.trim() : value;
            if (trimmedValue === '') return true;
            const { country } = this.parent;
            // US and US Territories allow ZIP+4 format (10 chars with hyphen)
            if (isUS(country) || isUSTerritory(country)) {
              return trimmedValue.length <= 10;
            }
            return trimmedValue.length <= 9;
          })
          .test('format-validation', function (value) {
            if (!value) return true;
            const trimmedValue = typeof value === 'string' ? value.trim() : value;
            if (trimmedValue === '') return true;
            const { country } = this.parent;

            const error = validatePostalCodeFormat(trimmedValue, country);
            if (error) {
              return this.createError({ message: error });
            }
            return true;
          })
          .when('country', {
            is: (country: string) => requiresStateAndPostalCode(country || 'US'),
            then: (schema) => schema.required(requiredFieldMessage('ZIP/Postal Code')),
            otherwise: (schema) => schema.notRequired(),
          }),
      }),
    otherwise: (schema) => schema.notRequired(),
  }),
  foreignPrincipalAddress: yup.object().when(['personType', 'addressType', 'foreignBusinessNoUSLocation'], {
    is: (personType: string, addressType: string, foreignBusinessNoUSLocation: boolean | string) =>
      personType === PersonType.LEGAL_ENTITY &&
      addressType === AddressType.PRIMARY &&
      !isForeignBusinessToggleOn(foreignBusinessNoUSLocation),
    then: () =>
      yup.object().shape({
        address1: yup
          .string()
          .trim()
          .required(requiredFieldMessage('Address 1'))
          .max(100, 'Address 1 cannot exceed 100 characters'),
        address2: yup.string().trim().max(100, 'Address 2 cannot exceed 100 characters').notRequired(),
        city: yup.string().trim().required(requiredFieldMessage('City')).max(50, 'City cannot exceed 50 characters'),
        country: yup.string().required(requiredFieldMessage('Country/Jurisdiction')),
        state: yup.string().when('country', {
          is: (country: string) => requiresStateAndPostalCode(country || 'US'),
          then: (schema) =>
            schema
              .trim()
              .required(requiredFieldMessage('State/U.S. Territory'))
              .test('valid-state-for-country', function (value) {
                const { country } = this.parent;
                if (!value || !country) return true;

                if (!isValidStateForCountry(value, country)) {
                  return this.createError({
                    message: 'Invalid state/province code for the selected country',
                  });
                }
                return true;
              }),
          otherwise: (schema) => schema.trim().notRequired(),
        }),
        postalCode: yup
          .string()
          .trim()
          .test('max-length', 'Postal Code cannot exceed 9 characters', function (value) {
            if (!value) return true;
            const trimmedValue = typeof value === 'string' ? value.trim() : value;
            if (trimmedValue === '') return true;
            const { country } = this.parent;
            // US and US Territories allow ZIP+4 format (10 chars with hyphen)
            if (isUS(country) || isUSTerritory(country)) {
              return trimmedValue.length <= 10;
            }
            return trimmedValue.length <= 9;
          })
          .test('format-validation', function (value) {
            if (!value) return true;
            const trimmedValue = typeof value === 'string' ? value.trim() : value;
            if (trimmedValue === '') return true;
            const { country } = this.parent;

            const error = validatePostalCodeFormat(trimmedValue, country);
            if (error) {
              return this.createError({ message: error });
            }
            return true;
          })
          .when('country', {
            is: (country: string) => requiresStateAndPostalCode(country || 'US'),
            then: (schema) => schema.required(requiredFieldMessage('ZIP/Postal Code')),
            otherwise: (schema) => schema.notRequired(),
          }),
      }),
    otherwise: (schema) => schema.notRequired(),
  }),
  primaryUSAddress: yup.object().when(['personType', 'addressType', 'foreignBusinessNoUSLocation'], {
    is: (personType: string, addressType: string, foreignBusinessNoUSLocation: boolean | string) =>
      personType === PersonType.LEGAL_ENTITY &&
      (addressType === AddressType.PRIMARY || addressType === AddressType.FOREIGN) &&
      !isForeignBusinessToggleOn(foreignBusinessNoUSLocation),
    then: () =>
      yup.object().shape({
        address1: yup
          .string()
          .trim()
          .required(requiredFieldMessage('Address 1'))
          .max(100, 'Address 1 cannot exceed 100 characters'),
        address2: yup.string().trim().max(100, 'Address 2 cannot exceed 100 characters').notRequired(),
        city: yup.string().trim().required(requiredFieldMessage('City')).max(50, 'City cannot exceed 50 characters'),
        country: yup.string().required(requiredFieldMessage('Country/Jurisdiction')),
        state: yup.string().when('country', {
          is: (country: string) => requiresStateAndPostalCode(country || 'US'),
          then: (schema) =>
            schema
              .trim()
              .required(requiredFieldMessage('State/U.S. Territory'))
              .test('valid-state-for-country', function (value) {
                const { country } = this.parent;
                if (!value || !country) return true;

                if (!isValidStateForCountry(value, country)) {
                  return this.createError({
                    message: 'Invalid state/province code for the selected country',
                  });
                }
                return true;
              }),
          otherwise: (schema) => schema.trim().notRequired(),
        }),
        postalCode: yup
          .string()
          .trim()
          .test('max-length', 'Postal Code cannot exceed 9 characters', function (value) {
            if (!value) return true;
            const trimmedValue = typeof value === 'string' ? value.trim() : value;
            if (trimmedValue === '') return true;
            const { country } = this.parent;
            // US and US Territories allow ZIP+4 format (10 chars with hyphen)
            if (isUS(country) || isUSTerritory(country)) {
              return trimmedValue.length <= 10;
            }
            return trimmedValue.length <= 9;
          })
          .test('format-validation', function (value) {
            if (!value) return true;
            const trimmedValue = typeof value === 'string' ? value.trim() : value;
            if (trimmedValue === '') return true;
            const { country } = this.parent;

            const error = validatePostalCodeFormat(trimmedValue, country);
            if (error) {
              return this.createError({ message: error });
            }
            return true;
          })
          .when('country', {
            is: (country: string) => requiresStateAndPostalCode(country || 'US'),
            then: (schema) => schema.required(requiredFieldMessage('ZIP/Postal Code')),
            otherwise: (schema) => schema.notRequired(),
          }),
      }),
    otherwise: (schema) => schema.notRequired(),
  }),
});

export const transferorSchema = yup.object().shape({
  transferorType: yup
    .string()
    .oneOf([TransferorType.INDIVIDUAL, TransferorType.ENTITY, TransferorType.TRUST])
    .required(requiredFieldMessage('Transferor Type')),

  firstName: yup
    .string()
    .trim()
    .max(50, 'First Name cannot exceed 50 characters')
    .when('transferorType', {
      is: TransferorType.INDIVIDUAL,
      then: (schema) => schema.required(requiredFieldMessage('First Name')),
      otherwise: (schema) => schema.notRequired(),
    }),
  middleName: yup.string().trim().max(50, 'Middle Name cannot exceed 50 characters').notRequired(),
  lastName: yup
    .string()
    .trim()
    .max(50, 'Last Name cannot exceed 50 characters')
    .when('transferorType', {
      is: TransferorType.INDIVIDUAL,
      then: (schema) => schema.required(requiredFieldMessage('Last Name')),
      otherwise: (schema) => schema.notRequired(),
    }),
  suffix: yup.string().trim().max(20, 'Suffix cannot exceed 20 characters').notRequired(),
  dateOfBirth: yup.date().when('transferorType', {
    is: TransferorType.INDIVIDUAL,
    then: (schema) =>
      schema
        .max(new Date(), 'Date of Birth cannot be in the future')
        .typeError('Please enter a valid date')
        .required(requiredFieldMessage('Date of Birth')),
    otherwise: (schema) => schema.notRequired(),
  }),

  entityLegalName: yup
    .string()
    .trim()
    .max(130, 'Legal Name cannot exceed 130 characters')
    .when('transferorType', {
      is: (value: string) => value === TransferorType.ENTITY || value === TransferorType.TRUST,
      then: (schema) => schema.required(requiredFieldMessage('Legal Name')),
      otherwise: (schema) => schema.notRequired(),
    }),
  alternateNames: yup
    .mixed()
    .nullable()
    .when('transferorType', {
      is: TransferorType.ENTITY,
      then: (schema) =>
        schema.test('max-length-per-name', 'Alternate Name cannot exceed 150 characters', function (value) {
          if (!value) return true;
          let names: string[] = [];
          if (typeof value === 'string') {
            names = value.split(',').map((name) => name.trim());
          } else if (Array.isArray(value)) {
            names = value.map((name) => (typeof name === 'string' ? name.trim() : ''));
          } else {
            return true;
          }
          return names.every((name) => name.length <= 150);
        }),
      otherwise: (schema) => schema.notRequired(),
    }),

  dateTrustExecuted: yup
    .date()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .when('transferorType', {
      is: TransferorType.TRUST,
      then: (schema) =>
        schema
          .max(new Date(), 'Date Trust Instrument was Executed cannot be in the future')
          .typeError('Please enter a valid date')
          .required(requiredFieldMessage('Date Trust Instrument was Executed')),
      otherwise: (schema) => schema.notRequired(),
    }),

  address: yup.object().when(['transferorType', 'addressType', 'foreignBusinessNoUSLocation'], {
    is: (transferorType: string, addressType: string, foreignBusinessNoUSLocation: boolean | string) =>
      transferorType === TransferorType.INDIVIDUAL ||
      (transferorType === TransferorType.ENTITY &&
        addressType === AddressType.FOREIGN &&
        !isForeignBusinessToggleOn(foreignBusinessNoUSLocation)),
    then: () =>
      yup.object().shape({
        address1: yup
          .string()
          .trim()
          .required(requiredFieldMessage('Address 1'))
          .max(100, 'Address 1 cannot exceed 100 characters'),
        address2: yup.string().trim().max(100, 'Address 2 cannot exceed 100 characters').notRequired(),
        city: yup.string().trim().required(requiredFieldMessage('City')).max(50, 'City cannot exceed 50 characters'),
        country: yup.string().required(requiredFieldMessage('Country/Jurisdiction')),
        state: yup.string().when('country', {
          is: (country: string) => requiresStateAndPostalCode(country),
          then: (schema) =>
            schema
              .trim()
              .required(requiredFieldMessage('State/U.S. Territory'))
              .test('valid-state-for-country', function (value) {
                const { country } = this.parent;
                if (!value || !country) return true;

                if (!isValidStateForCountry(value, country)) {
                  return this.createError({
                    message: 'Invalid state/province code for the selected country',
                  });
                }
                return true;
              }),
          otherwise: (schema) => schema.trim().notRequired(),
        }),
        postalCode: yup
          .string()
          .trim()
          .test('max-length', 'Postal Code cannot exceed 9 characters', function (value) {
            if (!value) return true;
            const trimmedValue = typeof value === 'string' ? value.trim() : value;
            if (trimmedValue === '') return true;
            const { country } = this.parent;
            // US and US Territories allow ZIP+4 format (10 chars with hyphen)
            if (isUS(country) || isUSTerritory(country)) {
              return trimmedValue.length <= 10;
            }
            return trimmedValue.length <= 9;
          })
          .test('format-validation', function (value) {
            if (!value) return true;
            const trimmedValue = typeof value === 'string' ? value.trim() : value;
            if (trimmedValue === '') return true;
            const { country } = this.parent;

            const error = validatePostalCodeFormat(trimmedValue, country);
            if (error) {
              return this.createError({ message: error });
            }
            return true;
          })
          .when('country', {
            is: (country: string) => requiresStateAndPostalCode(country),
            then: (schema) => schema.required(requiredFieldMessage('ZIP/Postal Code')),
            otherwise: (schema) => schema.notRequired(),
          }),
      }),
    otherwise: () => addressSchema,
  }),

  addressType: yup.string().when(['transferorType', 'foreignBusinessNoUSLocation'], {
    is: (transferorType: string, foreignBusinessNoUSLocation: boolean | string) =>
      transferorType === TransferorType.ENTITY && !isForeignBusinessToggleOn(foreignBusinessNoUSLocation),
    then: (schema) => schema.required(requiredFieldMessage('Address Type')),
    otherwise: (schema) => schema.notRequired(),
  }),

  foreignBusinessNoUSLocation: yup.boolean().notRequired(),
  foreignBusinessAddress: yup.object().when(['transferorType', 'foreignBusinessNoUSLocation'], {
    is: (transferorType: string, foreignBusinessNoUSLocation: boolean | string) =>
      transferorType === TransferorType.ENTITY && isForeignBusinessToggleOn(foreignBusinessNoUSLocation),
    then: () =>
      yup.object().shape({
        address1: yup
          .string()
          .trim()
          .required(requiredFieldMessage('Address 1'))
          .max(100, 'Address 1 cannot exceed 100 characters'),
        address2: yup.string().trim().max(100, 'Address 2 cannot exceed 100 characters').notRequired(),
        city: yup.string().trim().required(requiredFieldMessage('City')).max(50, 'City cannot exceed 50 characters'),
        country: yup.string().required(requiredFieldMessage('Country/Jurisdiction')),
        state: yup.string().when('country', {
          is: (country: string) => requiresStateAndPostalCode(country),
          then: (schema) =>
            schema
              .trim()
              .required(requiredFieldMessage('State/U.S. Territory'))
              .test('valid-state-for-country', function (value) {
                const { country } = this.parent;
                if (!value || !country) return true;

                if (!isValidStateForCountry(value, country)) {
                  return this.createError({
                    message: 'Invalid state/province code for the selected country',
                  });
                }
                return true;
              }),
          otherwise: (schema) => schema.trim().notRequired(),
        }),
        postalCode: yup
          .string()
          .trim()
          .test('max-length', 'Postal Code cannot exceed 9 characters', function (value) {
            if (!value) return true;
            const trimmedValue = typeof value === 'string' ? value.trim() : value;
            if (trimmedValue === '') return true;
            const { country } = this.parent;
            // US and US Territories allow ZIP+4 format (10 chars with hyphen)
            if (isUS(country) || isUSTerritory(country)) {
              return trimmedValue.length <= 10;
            }
            return trimmedValue.length <= 9;
          })
          .test('format-validation', function (value) {
            if (!value) return true;
            const trimmedValue = typeof value === 'string' ? value.trim() : value;
            if (trimmedValue === '') return true;
            const { country } = this.parent;

            const error = validatePostalCodeFormat(trimmedValue, country);
            if (error) {
              return this.createError({ message: error });
            }
            return true;
          })
          .when('country', {
            is: (country: string) => requiresStateAndPostalCode(country),
            then: (schema) => schema.required(requiredFieldMessage('ZIP/Postal Code')),
            otherwise: (schema) => schema.notRequired(),
          }),
      }),
    otherwise: (schema) => schema.notRequired(),
  }),
  foreignPrincipalAddress: yup.object().when(['transferorType', 'addressType', 'foreignBusinessNoUSLocation'], {
    is: (transferorType: string, addressType: string, foreignBusinessNoUSLocation: boolean | string) =>
      transferorType === TransferorType.ENTITY &&
      addressType === AddressType.PRIMARY &&
      !isForeignBusinessToggleOn(foreignBusinessNoUSLocation),
    then: () =>
      yup.object().shape({
        address1: yup
          .string()
          .trim()
          .required(requiredFieldMessage('Address 1'))
          .max(100, 'Address 1 cannot exceed 100 characters'),
        address2: yup.string().trim().max(100, 'Address 2 cannot exceed 100 characters').notRequired(),
        city: yup.string().trim().required(requiredFieldMessage('City')).max(50, 'City cannot exceed 50 characters'),
        country: yup.string().required(requiredFieldMessage('Country/Jurisdiction')),
        state: yup.string().when('country', {
          is: (country: string) => requiresStateAndPostalCode(country),
          then: (schema) =>
            schema
              .trim()
              .required(requiredFieldMessage('State/U.S. Territory'))
              .test('valid-state-for-country', function (value) {
                const { country } = this.parent;
                if (!value || !country) return true;

                if (!isValidStateForCountry(value, country)) {
                  return this.createError({
                    message: 'Invalid state/province code for the selected country',
                  });
                }
                return true;
              }),
          otherwise: (schema) => schema.trim().notRequired(),
        }),
        postalCode: yup
          .string()
          .trim()
          .test('max-length', 'Postal Code cannot exceed 9 characters', function (value) {
            if (!value) return true;
            const trimmedValue = typeof value === 'string' ? value.trim() : value;
            if (trimmedValue === '') return true;
            const { country } = this.parent;
            // US and US Territories allow ZIP+4 format (10 chars with hyphen)
            if (isUS(country) || isUSTerritory(country)) {
              return trimmedValue.length <= 10;
            }
            return trimmedValue.length <= 9;
          })
          .test('format-validation', function (value) {
            if (!value) return true;
            const trimmedValue = typeof value === 'string' ? value.trim() : value;
            if (trimmedValue === '') return true;
            const { country } = this.parent;

            const error = validatePostalCodeFormat(trimmedValue, country);
            if (error) {
              return this.createError({ message: error });
            }
            return true;
          })
          .when('country', {
            is: (country: string) => requiresStateAndPostalCode(country),
            then: (schema) => schema.required(requiredFieldMessage('ZIP/Postal Code')),
            otherwise: (schema) => schema.notRequired(),
          }),
      }),
    otherwise: (schema) => schema.notRequired(),
  }),
  // primaryUSAddress is required when:
  // - Entity with addressType PRIMARY or FOREIGN, AND foreignBusinessNoUSLocation is NOT true
  primaryUSAddress: yup.object().when(['transferorType', 'addressType', 'foreignBusinessNoUSLocation'], {
    is: (transferorType: string, addressType: string, foreignBusinessNoUSLocation: boolean | string) =>
      transferorType === TransferorType.ENTITY &&
      (addressType === AddressType.PRIMARY || addressType === AddressType.FOREIGN) &&
      !isForeignBusinessToggleOn(foreignBusinessNoUSLocation),
    then: () =>
      yup.object().shape({
        address1: yup
          .string()
          .trim()
          .required(requiredFieldMessage('Address 1'))
          .max(100, 'Address 1 cannot exceed 100 characters'),
        address2: yup.string().trim().max(100, 'Address 2 cannot exceed 100 characters').notRequired(),
        city: yup.string().trim().required(requiredFieldMessage('City')).max(50, 'City cannot exceed 50 characters'),
        country: yup.string().required(requiredFieldMessage('Country/Jurisdiction')),
        state: yup.string().when('country', {
          is: (country: string) => requiresStateAndPostalCode(country),
          then: (schema) =>
            schema
              .trim()
              .required(requiredFieldMessage('State/U.S. Territory'))
              .test('valid-state-for-country', function (value) {
                const { country } = this.parent;
                if (!value || !country) return true;

                if (!isValidStateForCountry(value, country)) {
                  return this.createError({
                    message: 'Invalid state/province code for the selected country',
                  });
                }
                return true;
              }),
          otherwise: (schema) => schema.trim().notRequired(),
        }),
        postalCode: yup
          .string()
          .trim()
          .test('max-length', 'Postal Code cannot exceed 9 characters', function (value) {
            if (!value) return true;
            const trimmedValue = typeof value === 'string' ? value.trim() : value;
            if (trimmedValue === '') return true;
            const { country } = this.parent;
            // US and US Territories allow ZIP+4 format (10 chars with hyphen)
            if (isUS(country) || isUSTerritory(country)) {
              return trimmedValue.length <= 10;
            }
            return trimmedValue.length <= 9;
          })
          .test('format-validation', function (value) {
            if (!value) return true;
            const trimmedValue = typeof value === 'string' ? value.trim() : value;
            if (trimmedValue === '') return true;
            const { country } = this.parent;

            const error = validatePostalCodeFormat(trimmedValue, country);
            if (error) {
              return this.createError({ message: error });
            }
            return true;
          })
          .when('country', {
            is: (country: string) => requiresStateAndPostalCode(country),
            then: (schema) => schema.required(requiredFieldMessage('ZIP/Postal Code')),
            otherwise: (schema) => schema.notRequired(),
          }),
      }),
    otherwise: (schema) => schema.notRequired(),
  }),
  identification: identificationSchema,

  trustees: yup.array().when('transferorType', {
    is: TransferorType.TRUST,
    then: (schema) => schema.of(trusteeSchema).max(8, 'Maximum of 8 trustees allowed'),
    otherwise: (schema) => schema.notRequired(),
  }),
});
